import { AuthAPI, KeyLevelsAuth, RequestContext } from './types';
import { InitParamsState, InternalChatState } from 'types';
import { getCookieValue } from 'utils/cookies';
import { i18n } from 'i18n';
import { providerConfState } from 'store/providerConf';

export const buildRequestContext = (chatState: InternalChatState, initParams: InitParamsState): RequestContext => {
  return {
    externalUserId: sessionStorage.getItem('external-user-id') ?? undefined,
    temporalUserId: chatState.userInfo?.temporalUserId ?? localStorage.getItem('temporal-user-id') ?? undefined,
    sandbox: initParams.sandbox,
    mode: initParams.mode,
    assistantId: initParams.assistantId,
    locale: i18n.getLocale(),
  };
};

const getRequestId = (prefix: string) => {
  return `${prefix}-${Math.floor(10000 + Math.random() * 90000)}`;
};

export const getBaseHeaders = (sandbox: boolean = false, prefixService: string) => {
  const cookieXvo = getCookieValue('X-Version-Override');
  const forceHeaders = false;
  return {
    'X-Client': 'frontend-tourPilot',
    'Content-Type': 'application/json',
    'X-Request-Id': getRequestId(prefixService),
    ...(sandbox && forceHeaders ? { 'xdesp-sandbox': 'true', 'X-Version-Override': cookieXvo ?? 'chappie=test|whisperers=test|dembed=test' } : {}),
  };
};

export const getDomain = () => {
  const domain = window.location.host;
  const isDev = domain.indexOf('localhost:') !== -1;

  if (isDev) {
    return `${process.env.API_HOST_DEV}`;
  }

  return `//sofia.despegar.com`;
};

export const getPath = (sandbox: boolean = false) => {
  return window.location.href.indexOf('/tourpilot') !== -1 && !sandbox ? 'chappie' : 'chappie';
};

/**
 * Resolve auth levels for internal use
 */
const getAuthApi = (): KeyLevelsAuth | undefined => {
  const level1Name = providerConfState.conf.auth?.cookieLevel1 ?? 'not_defined',
    level2Name = providerConfState.conf.auth?.cookieLevel2 ?? 'not_defined',
    isDevelopment = process.env.IS_DEVELOPMENT,
    dssHardcoded = process.env.DSS,
    dss2Hardcoded = process.env.DSS2,
    useEnvVariable = level1Name === 'dss' && level2Name === 'dss2' && dssHardcoded !== '' && dss2Hardcoded !== '' && isDevelopment === 'true',
    level1 = useEnvVariable ? dssHardcoded : getCookieValue(level1Name),
    level2 = useEnvVariable ? dss2Hardcoded : getCookieValue(level2Name),
    hasLevel1 = typeof level1 === 'string' && level1.length > 0,
    hasLevel2 = typeof level2 === 'string' && level2.length > 0;

  //console.debug(`Auth level 1 getCookieValue(${level1Name}) value:`, level1);
  //console.debug(`Auth level 2 getCookieValue(${level2Name}) value:`, level2);

  return hasLevel1 || hasLevel2
    ? {
        ...(hasLevel1 ? { '1': { tokenName: level1Name, tokenValue: level1 } } : {}),
        ...(hasLevel2 ? { '2': { tokenName: level2Name, tokenValue: level2 } } : {}),
      }
    : undefined;
};

/**
 * Resolve auth levels for headers
 */
export const getAuthApiHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const authApi = getAuthApi();

  if (authApi) {
    Object.entries(authApi).forEach(([level, { tokenName, tokenValue }]) => {
      headers[`X-Auth-Level-${level}`] = tokenValue;
      headers[`X-Auth-Token-${level}`] = tokenName;
    });
  }

  return headers;
};

/**
 * Resolve auth levels for object
 */
export const getAuthApiObject = (): AuthAPI | undefined => {
  const authApi = getAuthApi();

  if (typeof authApi === 'undefined') {
    return undefined;
  }

  return Object.entries(authApi).reduce(
    (acc, [level, { tokenName, tokenValue }]) => {
      acc.keyLevels[level] = { tokenName, tokenValue };
      return acc;
    },
    { keyLevels: {} } as AuthAPI,
  );
};

export const getSofinatorDomain = () => {
  const domain = window.location.host;
  const isDev = domain.indexOf('localhost:') !== -1;
  const wsDomain = isDev ? 'ws://localhost:8081/sofinator/start-conversation' : 'wss://sofia.despegar.com/sofinator/start-conversation';
  return wsDomain;
};
